'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@/lib/supabase/client';
import { CandidateProfile } from '@/lib/types/candidate';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Calendar,
  FileText,
  User,
  BrainIcon,
  Code,
  Sparkle,
  ExternalLink,
  Award,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  Video,
  Briefcase,
  FolderOpen,
  GraduationCap
} from 'lucide-react';
import { formatTime } from '@/lib/utils/general';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { getLeadingInterviewTakenByCandidateId } from '@/lib/api/interview-taken';
import { ReviewTab } from './ReviewTab';
import { CodingAssessmentResults } from './CodingAssessmentResults';
import { InterviewAudioPlayer } from './InterviewAudioPlayer';
import { JobAssessments } from './JobAssessments';

type TranscriptEntry = {
  role?: string;
  type?: string;
  message?: string;
  [key: string]: any;
};

type InterviewResult = {
  id: string;
  candidate_id: string;
  interview_id: string;
  transcript: TranscriptEntry[] | string;
  summary: string;
  status?: string;
  score?: number;
  created_at: string;
  conversation_id?: string;
  interviews?: {
    title: string;
  };
  code_review_results?: any;
  wrap_up_transcript?: TranscriptEntry[] | string;
};

interface CandidateProject {
  name: string;
  description?: string;
  url?: string;
}

type CandidateProfileWithProjects = CandidateProfile & {
  projects?: CandidateProject[];
  location?: string;
  github_url?: string;
  linkedin_url?: string;
};

// Coding Assessment Types
interface ChallengeDetail {
  title: string;
  date: string;
  language: string;
  score: number;
  time_taken: number;
  session_recordings?: string[];
  meta?: {
    difficulty?: string;
    tags?: string[];
  };
}

interface McDetail {
  id: string;
  question: string;
  answer: string;
  correct: boolean;
  meta?: {
    tags?: string[];
  };
}

interface CheatingDetails {
  tab_leaving?: number;
  plagiarism?: string;
  pasted_code?: string;
  suspicious_activity?: boolean;
  ai_usage?: boolean;
}

interface AssessmentResult {
  total_challenges: number;
  mc_score: number;
  code_score: number;
  final_score: number;
  qualified: boolean;
  qualifying_score: number;
  cheating_flag?: string;
  cheating_details?: CheatingDetails;
  challenge_details?: ChallengeDetail[];
  mc_details?: McDetail[];
  scorecard?: Record<string, number>;
  time_taken?: number;
  admin_notes?: any;
}

interface AssessmentData {
  id: string;
  status: string;
  invited_at?: string;
  completed_at?: string;
  coding_assessment_results?: AssessmentResult[];
}

interface CandidateDetailProps {
  candidateId: string;
  onCreateContract?: (candidateId: string) => void;
  onAddToFavorites?: (candidateId: string) => void;
  showQualifiedCandidate?: boolean;
  isPublic?: boolean;
}

export function CandidateDetail({
  candidateId,
  onCreateContract,
  onAddToFavorites,
  showQualifiedCandidate,
  isPublic = false
}: CandidateDetailProps) {
  const [candidate, setCandidate] =
    useState<CandidateProfileWithProjects | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInterview, setSelectedInterview] =
    useState<InterviewResult | null>(null);
  const [conversationDetails, setConversationDetails] = useState<any>(null);
  const [conversationAudioUrl, setConversationAudioUrl] = useState<string>('');

  const [activeTab, setActiveTab] = useState('summary');
  const [activeSubtab, setActiveSubtab] = useState('round1');

  // Sync activeSubtab when assessments tab becomes active
  useEffect(() => {
    if (activeTab === 'assessments' && activeSubtab !== 'round1') {
      setActiveSubtab('round1');
    }
  }, [activeTab]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const [seekToTimestamp, setSeekToTimestamp] = useState<number | undefined>();
  const [isCodingChallengeExpanded, setIsCodingChallengeExpanded] =
    useState(false);
  // Removed audioRef - now handled by InterviewAudioPlayer component

  // Interview recording video URL (if available)
  const [interviewVideoUrl, setInterviewVideoUrl] = useState<string | null>(null);

  const [isCalculationsOpen, setIsCalculationsOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | undefined>(
    undefined
  );

  // Coding Assessment State
  const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(
    null
  );
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState<string | null>(null);

  // Evaluation State
  const [evaluation, setEvaluation] = useState<any>({});

  const router = useRouter();
  const supabase = createClient();

  const formatChallengeTitle = (title: string) => {
    return title
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Cleanup blob URL on unmount or URL change
  useEffect(() => {
    return () => {
      if (conversationAudioUrl && conversationAudioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(conversationAudioUrl);
      }
    };
  }, [conversationAudioUrl]);

  // Fetch coding assessment data
  const fetchAssessmentData = async (interviewTakenId: string) => {
    try {
      setAssessmentLoading(true);
      setAssessmentError(null);

      // First, fetch the coding assessment for this interview
      const { data: assessment, error: assessmentError } = await supabase
        .from('coding_assessments')
        .select(
          `
          id,
          status,
          invited_at,
          completed_at,
          test_id,
          assessment_id,
          invite_url,
          report_url
        `
        )
        .eq('interview_taken_id', interviewTakenId)
        .maybeSingle();

      if (assessmentError) {
        console.error('Error fetching assessment:', assessmentError);
        setAssessmentError('Failed to fetch coding assessment data');
        return;
      }

      if (!assessment) {
        // No coding assessment found - this is normal for some interviews
        setAssessmentData(null);
        return;
      }


      // Then fetch the results using the assessment id
      const { data: results, error: resultsError } = await supabase
        .from('coding_assessment_results')
        .select(
          `
          total_challenges,
          mc_score,
          code_score,
          final_score,
          qualified,
          qualifying_score,
          cheating_flag,
          cheating_details,
          challenge_details,
          mc_details,
          scorecard,
          time_taken,
          admin_notes,
          raw_response
        `
        )
        .eq('assessment_id', assessment.id)
        .maybeSingle();

      if (resultsError) {
        console.error('Error fetching assessment results:', resultsError);
        setAssessmentError('Failed to fetch coding assessment results');
        return;
      }


        // Combine the assessment and results data
        const combinedData = {
          ...assessment,
          coding_assessment_results: results ? [results] : []
        };


        setAssessmentData(combinedData);
    } catch (err) {
      console.error('Error in fetchAssessmentData:', err);
      setAssessmentError(
        err instanceof Error ? err.message : 'Failed to load assessment results'
      );
    } finally {
      setAssessmentLoading(false);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const { data: candidateData, error: candidateError } = await supabase
          .from('candidates')
          .select('*')
          .eq('id', candidateId)
          .single();

        if (candidateError) throw candidateError;
        if (!candidateData) throw new Error('Candidate not found');


        setCandidate(candidateData);

        const interview = await getLeadingInterviewTakenByCandidateId(
          candidateId,
          showQualifiedCandidate
        );



        if (interview) {
          setSelectedInterview(interview);

          // Fetch coding assessment data
          await fetchAssessmentData(interview.id);

          // Fetch evaluation data
          try {
            const evaluationRes = await fetch(
              `/api/interviews-taken/${interview.id}/evaluations`
            );
            
            if (evaluationRes.ok) {
              const evaluationData = await evaluationRes.json();
              setEvaluation(evaluationData.data || {});
            }
          } catch (error) {
            console.error('❌ Error fetching evaluation data:', error);
          }

          if (interview.conversation_id) {
            try {
            const [detailsRes, audioRes] = await Promise.all([
              fetch(
                `/api/elevenlabs/conversation/${interview.conversation_id}`
              ),
              fetch(
                `/api/elevenlabs/conversation/${interview.conversation_id}/audio`
              )
            ]);

            if (detailsRes.ok) {
              const details = await detailsRes.json();
              setConversationDetails(details);
            }

            if (audioRes.ok) {
              // Get the blob and ensure it's fully loaded
              const audioBlob = await audioRes.blob();

              // Create a new blob with explicit MIME type to ensure proper handling
              const typedBlob = new Blob([audioBlob], { type: 'audio/mpeg' });
              const audioUrl = URL.createObjectURL(typedBlob);

              setConversationAudioUrl(audioUrl);
            }
            } catch (error) {
              console.error('❌ Error fetching interview details:', error);
            }
          }

          // Attempt to fetch interview recording video URL (if available)
          try {
            const userId = candidateData.user_id;
            if (userId) {
              const videoRes = await fetch(`/api/interviews-taken/${interview.id}/video?userId=${userId}`);
              
              if (videoRes.ok) {
                const { url } = await videoRes.json();
                if (url) setInterviewVideoUrl(url);
              } else {
                setInterviewVideoUrl(null);
              }
            }
          } catch (error) {
            console.error('❌ Error fetching video:', error);
            setInterviewVideoUrl(null);
          }

        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [candidateId, supabase, showQualifiedCandidate]);

  // Audio event handling is now managed by InterviewAudioPlayer component

  // Play/pause is now handled by the InterviewAudioPlayer component

  const getInitials = (name: string) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(n => n[0])
      .join('');
  };

  const getYearsOfExperience = () => {
    if (!candidate?.work_experiences?.length) return 'No experience';

    const totalYears = candidate.work_experiences.reduce((acc, exp) => {
      const startDate = new Date(exp.endDate || new Date());
      const endDate = new Date(exp.startDate || new Date());
      const years =
        endDate.getFullYear() -
        startDate.getFullYear() +
        (endDate.getMonth() - startDate.getMonth()) / 12;
      return acc + years;
    }, 0);

    const roundedYears = Math.round(totalYears);
    if (roundedYears === 1) return '1 year exp';
    if (roundedYears > 1) return `${roundedYears} years exp`;
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-3 space-y-6">
              {/* Header Skeleton - matches actual header */}
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <Skeleton className="w-16 h-16 rounded-full" />
                      <div>
                        <Skeleton className="h-6 w-48 mb-1" />
                        <div className="flex items-center gap-2 mt-1">
                          <Skeleton className="h-4 w-24" />
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <Skeleton className="h-4 w-32" />
                          <div className="flex gap-2">
                            <Skeleton className="h-6 w-20 rounded-full" />
                            <Skeleton className="h-6 w-20 rounded-full" />
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Overall Score Circle */}
                    <div className="text-center">
                      <div className="relative">
                        <Skeleton className="w-20 h-20 rounded-full" />
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Tabs Skeleton - matches actual tabs */}
              <div className="flex items-center gap-3 bg-gray-50/50 border border-gray-200 backdrop-blur-lg py-1 px-1 rounded-full shadow-sm sticky top-0 z-10 h-auto">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex-1">
                    <Skeleton className="h-10 w-full rounded-full" />
                </div>
                ))}
                </div>

              {/* Content Skeleton - matches actual summary structure */}
              <div className="space-y-6">
                {/* Assessment Overview - matches exact structure */}
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl border border-slate-200/60 p-8">
                  <div className="mb-8">
                    <Skeleton className="h-8 w-64 mb-2" />
                    <Skeleton className="h-5 w-96" />
                  </div>
                  
                  {/* Assessment Metrics - matches exact grid */}
                  <div className="grid grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/50">
                        <div className="flex items-center justify-between mb-4">
                          <Skeleton className="w-10 h-10 rounded-lg" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-baseline gap-2">
                            <Skeleton className="h-8 w-12" />
                            <Skeleton className="h-4 w-8" />
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2">
                            <Skeleton className="h-2 w-3/4 rounded-full" />
                          </div>
                        </div>
                      </div>
                    ))}
              </div>
            </div>

                {/* Assessment Insights - matches exact structure */}
            <div className="space-y-6">
                  <Skeleton className="h-8 w-48" />
                  
                  <div className="grid gap-6">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-white rounded-xl border border-slate-200/60 p-6">
                        <div className="flex items-start gap-4">
                          <Skeleton className="w-12 h-12 rounded-xl" />
                          <div className="flex-1">
                            <Skeleton className="h-6 w-48 mb-3" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-3/4" />
                              <Skeleton className="h-4 w-1/2" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hiring Recommendation - matches exact structure */}
                <div className="space-y-6">
                  <Skeleton className="h-8 w-48" />
                  
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border border-emerald-200/60 p-8">
                    <div className="flex items-start gap-6">
                      <Skeleton className="w-16 h-16 rounded-2xl" />
                      <div className="flex-1">
                        <Skeleton className="h-8 w-48 mb-4" />
                        <div className="space-y-4">
                          <div>
                            <Skeleton className="h-5 w-32 mb-2" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-5/6" />
                              <Skeleton className="h-4 w-4/5" />
                            </div>
                          </div>
                          <div>
                            <Skeleton className="h-5 w-28 mb-2" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-3/4" />
                            </div>
                          </div>
                          <div>
                            <Skeleton className="h-5 w-24 mb-2" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-2/3" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>Error: {error || 'Candidate not found'}</p>
        {!isPublic && (
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => router.push('/company/candidates')}
          >
            Back to Candidates
          </Button>
        )}
      </div>
    );
  }

  return (
    <motion.div 
      className="min-h-screen bg-gray-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {/* Audio element replaced with InterviewAudioPlayer component */}
      <div className="px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div
            className="lg:col-span-3 space-y-6"
          >
            {/* Candidate Overview */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ 
                        duration: 0.2,
                        delay: 0.2
                      }}
                    >
                    <Avatar className="w-16 h-16">
                      <AvatarImage
                        src={candidate.profile_image_url ?? undefined}
                        alt={candidate.candidate_name ?? 'Candidate'}
                      />
                      <AvatarFallback className="text-lg">
                        {getInitials(candidate.candidate_name ?? 'C')}
                      </AvatarFallback>
                    </Avatar>
                    </motion.div>
                    <div>
                      <CardTitle className="text-xl font-domaine-narrow font-medium">
                        {candidate.candidate_name}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm font-medium text-gray-500">
                          {getYearsOfExperience()}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-sm text-gray-600">
                        <span>{candidate.location ?? 'Gujarat, India 🇮🇳'}</span>
                        <span>•</span>
                        <div className="flex gap-2">
                          <a
                            href={candidate.linkedin_url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block"
                          >
                          <Badge
                            variant="outline"
                              className="px-2 py-0.5 text-xs cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            <img
                              src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/81/LinkedIn_icon.svg/72px-LinkedIn_icon.svg.png?20210220164014"
                              alt="LinkedIn"
                              className="w-3 h-3 mr-1"
                            />
                            LinkedIn
                              <ExternalLink className="w-3 h-3 ml-1" />
                          </Badge>
                          </a>

                            <a
                              href={candidate.github_url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                            className="inline-block"
                          >
                            <Badge
                              variant="outline"
                              className="px-2 w-fit py-0.5 text-xs cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                            <img
                              src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/91/Octicons-mark-github.svg/640px-Octicons-mark-github.svg.png"
                              alt="GitHub"
                              className="w-3 h-3 mr-1"
                            />
                            GitHub
                              <ExternalLink className="w-3 h-3 ml-1" />
                          </Badge>
                          </a>
                        </div>
                      </div>

                      {process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' &&
                        !isPublic && (
                          <p className="text-sm text-gray-300 font-mono mt-2">
                            ID: {candidate.id}
                          </p>
                        )}
                    </div>
                  </div>
                  
                  {/* Overall Score */}
                  <div className="text-center">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full bg-white border-2 border-slate-200 flex flex-col items-center justify-center shadow-sm">
                        <span className="text-2xl font-semibold text-slate-800">
                          {(() => {
                            const experienceScore = evaluation?.experience_assessment?.candidateSeniorityScore || 0;
                            const englishScore = evaluation?.english_proficiency?.english_proficiency_score || 50;
                            const codingScore = assessmentData?.coding_assessment_results?.[0]?.final_score || 0;

                            if (experienceScore === 0 && englishScore === 50 && codingScore === 0) {
                              return 'N/A';
                            }

                            const totalScore = experienceScore * 0.35 + englishScore * 0.25 + codingScore * 0.4;
                            return Math.round(totalScore);
                          })()}
                        </span>
                        <span className="text-xs text-slate-500 font-medium">Overall</span>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>
            </motion.div>

            {/* Interview Content Tabs */}
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
              <TabsList className="flex items-center gap-3 bg-gray-50/50 border border-gray-200 backdrop-blur-lg px-1 rounded-lg shadow-sm sticky top-0 z-10 h-auto">
                <TabsTrigger 
                  value="summary" 
                  className="relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-lg transition-colors text-gray-600 hover:text-blue-600/80 data-[state=active]:text-blue-600/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                    <span className="font-medium">Summary</span>
                  </div>
                  {activeTab === "summary" && (
                    <motion.div
                      layoutId="lamp"
                      className="absolute inset-0 w-full bg-blue-500/5 rounded-lg -z-10"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    >
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-blue-500 rounded-lg">
                        <div className="absolute w-12 h-6 bg-blue-500/20 rounded-lg blur-md -top-2 -left-2" />
                        <div className="absolute w-8 h-6 bg-blue-500/20 rounded-lg blur-md -top-1" />
                        <div className="absolute w-4 h-4 bg-blue-500/20 rounded-lg blur-sm top-0 left-2" />
                      </div>
                    </motion.div>
                  )}
                </TabsTrigger>

                <TabsTrigger 
                  value="candidate-info" 
                  className="relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-lg transition-colors text-gray-600 hover:text-green-600/80 data-[state=active]:text-green-600/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                    <span className="font-medium">Candidate Info</span>
                  </div>
                  {activeTab === "candidate-info" && (
                    <motion.div
                      layoutId="lamp"
                      className="absolute inset-0 w-full bg-green-500/5 rounded-lg -z-10"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    >
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-green-500 rounded-lg">
                        <div className="absolute w-12 h-6 bg-green-500/20 rounded-lg blur-md -top-2 -left-2" />
                        <div className="absolute w-8 h-6 bg-green-500/20 rounded-lg blur-md -top-1" />
                        <div className="absolute w-4 h-4 bg-green-500/20 rounded-lg blur-sm top-0 left-2" />
                      </div>
                    </motion.div>
                  )}
                </TabsTrigger>

                <TabsTrigger
                  value="assessments"
                  className="relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-lg transition-colors text-gray-600 hover:text-orange-600/80 data-[state=active]:text-orange-600/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!selectedInterview}
                >
                  <div className="flex items-center gap-2">
                  <Award className="w-4 h-4" />
                    <span className="font-medium">Assessments</span>
                  </div>
                  {activeTab === "assessments" && (
                    <motion.div
                      layoutId="lamp"
                      className="absolute inset-0 w-full bg-orange-500/5 rounded-lg -z-10"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    >
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-orange-500 rounded-t-full">
                        <div className="absolute w-12 h-6 bg-orange-500/20 rounded-lg blur-md -top-2 -left-2" />
                        <div className="absolute w-8 h-6 bg-orange-500/20 rounded-lg blur-md -top-1" />
                        <div className="absolute w-4 h-4 bg-orange-500/20 rounded-lg blur-sm top-0 left-2" />
                      </div>
                    </motion.div>
                  )}
                </TabsTrigger>
                
                <TabsTrigger
                  value="review"
                  className="relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-lg transition-colors text-gray-600 hover:text-purple-600/80 data-[state=active]:text-purple-600/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={
                    !selectedInterview ||
                    selectedInterview.code_review_results?.status === 'failed'
                  }
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Sparkle className="w-3 h-3 text-purple-600" />
                    </div>
                    <span className="font-medium">AI Review</span>
                  </div>
                  {activeTab === "review" && (
                    <motion.div
                      layoutId="lamp"
                      className="absolute inset-0 w-full bg-purple-500/5 rounded-lg -z-10"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    >
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-purple-500 rounded-t-full">
                        <div className="absolute w-12 h-6 bg-purple-500/20 rounded-lg blur-md -top-2 -left-2" />
                        <div className="absolute w-8 h-6 bg-purple-500/20 rounded-lg blur-md -top-1" />
                        <div className="absolute w-4 h-4 bg-purple-500/20 rounded-lg blur-sm top-0 left-2" />
                      </div>
                    </motion.div>
                  )}
                </TabsTrigger>
              </TabsList>

              </motion.div>

              <AnimatePresence mode="wait">
                <TabsContent value="summary" className="space-y-6" key="summary">
                  <motion.div 
                    className="space-y-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                  {/* Assessment Overview */}
                  <div className="rounded-2xl border border-slate-200 bg-white p-8">
                     <div className="mb-8">
                       <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                              Assessment Overview
                            </h2>
                       <p className="text-slate-600">
                            Comprehensive evaluation across all assessment areas
                          </p>
                        </div>

                     {/* Assessment Metrics */}
                     <div className="grid grid-cols-3 gap-6">
                       <div className="bg-white rounded-xl p-6 border border-slate-200">
                         <div className="flex items-center justify-between mb-4">
                           <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center">
                             <BrainIcon className="w-5 h-5 text-slate-600" />
                          </div>
                           <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Experience</span>
                        </div>
                         <div className="space-y-2">
                           <div className="flex items-baseline gap-2">
                             <span className="text-2xl font-semibold text-slate-800">
                               {evaluation?.experience_assessment?.candidateSeniorityScore || 'N/A'}
                            </span>
                             <span className="text-sm text-slate-500">/ 100</span>
                          </div>
                           <div className="w-full bg-slate-100 rounded-full h-2">
                             <div 
                               className="bg-blue-500/70 h-2 rounded-full transition-all duration-500"
                               style={{ width: `${evaluation?.experience_assessment?.candidateSeniorityScore || 0}%` }}
                             />
                           </div>
                         </div>
                        </div>

                       <div className="bg-white rounded-xl p-6 border border-slate-200">
                         <div className="flex items-center justify-between mb-4">
                           <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center">
                             <User className="w-5 h-5 text-slate-600" />
                          </div>
                           <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Communication</span>
                         </div>
                         <div className="space-y-2">
                           <div className="flex items-baseline gap-2">
                             <span className="text-2xl font-semibold text-slate-800">
                               {evaluation?.english_proficiency?.english_proficiency_score || 'N/A'}
                            </span>
                             <span className="text-sm text-slate-500">/ 100</span>
                          </div>
                           <div className="w-full bg-slate-100 rounded-full h-2">
                             <div 
                               className="bg-emerald-500/70 h-2 rounded-full transition-all duration-500"
                               style={{ width: `${evaluation?.english_proficiency?.english_proficiency_score || 0}%` }}
                             />
                           </div>
                         </div>
                        </div>

                       <div className="bg-white rounded-xl p-6 border border-slate-200">
                         <div className="flex items-center justify-between mb-4">
                           <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center">
                             <Code className="w-5 h-5 text-slate-600" />
                          </div>
                           <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Technical</span>
                         </div>
                         <div className="space-y-2">
                           <div className="flex items-baseline gap-2">
                             <span className="text-2xl font-semibold text-slate-800">
                               {assessmentData?.coding_assessment_results?.[0]?.final_score || 'N/A'}
                            </span>
                             <span className="text-sm text-slate-500">/ 100</span>
                          </div>
                           <div className="w-full bg-slate-100 rounded-full h-2">
                             <div 
                               className="bg-purple-500/70 h-2 rounded-full transition-all duration-500"
                               style={{ width: `${assessmentData?.coding_assessment_results?.[0]?.final_score || 0}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                      </div>

                                        {/* Hiring Recommendation */}
                  <div className="space-y-6">
                    <h2 className="text-2xl font-semibold text-slate-900">Hiring Recommendation</h2>
                    
                        {(() => {
                      const experienceScore = evaluation?.experience_assessment?.candidateSeniorityScore || 0;
                      const englishScore = evaluation?.english_proficiency?.english_proficiency_score || 50;
                      const codingScore = assessmentData?.coding_assessment_results?.[0]?.final_score || 0;
                      const totalScore = experienceScore * 0.35 + englishScore * 0.25 + codingScore * 0.4;

                          if (totalScore >= 80) {
                            return (
                          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border border-emerald-200/60 p-8">
                            <div className="flex items-start gap-6">
                              <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                                  </div>
                              <div className="flex-1">
                                <h3 className="text-2xl font-semibold text-emerald-900 mb-4">
                                    Strong Candidate
                                </h3>
                                <div className="space-y-4 text-emerald-800">
                                  <div>
                                    <h4 className="font-medium text-emerald-900 mb-2">Overall Assessment</h4>
                                    <p className="leading-relaxed">
                                      Excellent performance across all areas with a {Math.round(totalScore)}/100 score. 
                                      This candidate demonstrates exceptional technical skills, strong communication abilities, 
                                      and the experience level needed for the role.
                                    </p>
                                </div>
                                  <div>
                                    <h4 className="font-medium text-emerald-900 mb-2">Recommendation</h4>
                                    <p className="leading-relaxed">
                                      Strongly recommend for immediate hire. Consider senior-level positions if experience score is particularly high.
                                    </p>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-emerald-900 mb-2">Next Steps</h4>
                                    <p className="leading-relaxed">
                                      Proceed to final interview or offer stage. This candidate is ready for immediate onboarding.
                                    </p>
                                  </div>
                                </div>
                              </div>
                                </div>
                              </div>
                            );
                          } else if (totalScore >= 60) {
                            return (
                          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200/60 p-8">
                            <div className="flex items-start gap-6">
                              <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                <AlertTriangle className="w-8 h-8 text-amber-600" />
                                  </div>
                              <div className="flex-1">
                                <h3 className="text-2xl font-semibold text-amber-900 mb-4">
                                    Promising Candidate
                                </h3>
                                <div className="space-y-4 text-amber-800">
                                  <div>
                                    <h4 className="font-medium text-amber-900 mb-2">Overall Assessment</h4>
                                    <p className="leading-relaxed">
                                      Solid fundamentals with room for growth. Score: {Math.round(totalScore)}/100. 
                                      Shows strong potential with proper guidance and mentorship.
                                    </p>
                                </div>
                                  <div>
                                    <h4 className="font-medium text-amber-900 mb-2">Recommendation</h4>
                                    <p className="leading-relaxed">
                                      Consider for mid-level positions with mentorship opportunities. Strong potential with proper guidance.
                                    </p>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-amber-900 mb-2">Next Steps</h4>
                                    <p className="leading-relaxed">
                                      Schedule follow-up interview to assess growth potential. Consider technical assessment or pair programming session.
                                    </p>
                                  </div>
                                </div>
                              </div>
                                </div>
                              </div>
                            );
                          } else {
                            return (
                          <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl border border-rose-200/60 p-8">
                            <div className="flex items-start gap-6">
                              <div className="w-12 h-12 rounded-2xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-2xl font-medium text-rose-600">×</span>
                                  </div>
                              <div className="flex-1">
                                <h3 className="text-2xl font-semibold text-rose-900 mb-4">
                                    Needs Development
                                </h3>
                                <div className="space-y-4 text-rose-800">
                                  <div>
                                    <h4 className="font-medium text-rose-900 mb-2">Overall Assessment</h4>
                                    <p className="leading-relaxed">
                                      Significant gaps in required skills. Score: {Math.round(totalScore)}/100. 
                                      Requires substantial development before being ready for the role.
                                  </p>
                                </div>
                                  <div>
                                    <h4 className="font-medium text-rose-900 mb-2">Recommendation</h4>
                                    <p className="leading-relaxed">
                                      Not recommended for current role. Consider for junior positions or internship programs with intensive training.
                            </p>
                          </div>
                                  <div>
                                    <h4 className="font-medium text-rose-900 mb-2">Next Steps</h4>
                                    <p className="leading-relaxed">
                                      Provide constructive feedback and suggest skill development resources. Consider re-assessment after 3-6 months of focused learning.
                            </p>
                          </div>
                            </div>
                          </div>
                                </div>
                              </div>
                            );
                          }
                        })()}
                      </div>

                  {/* Assessment Insights */}
                  <div className="space-y-6">
                    <h2 className="text-2xl font-semibold text-slate-900">Assessment Insights</h2>
                    
                    <div className="grid gap-6">
                      {/* Experience Assessment Summary */}
                      {evaluation?.experience_assessment?.executiveSummary && (
                        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                              <BrainIcon className="w-6 h-6 text-slate-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                                Experience Assessment
                              </h3>
                              <p className="text-slate-600 leading-relaxed">
                                {evaluation.experience_assessment.executiveSummary}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* English Proficiency Summary */}
                      {evaluation?.english_proficiency?.reasoning && (
                        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                              <User className="w-6 h-6 text-slate-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                                Communication Skills
                              </h3>
                              <p className="text-slate-600 leading-relaxed">
                                {evaluation.english_proficiency.reasoning}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Coding Assessment Summary */}
                      {assessmentData?.coding_assessment_results?.[0] && (
                        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                              <Code className="w-6 h-6 text-slate-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                                Technical Skills
                              </h3>
                              <div className="space-y-2">
                                <p className="text-slate-600 leading-relaxed">
                                  Final Score: <span className="font-medium text-slate-800">
                                    {assessmentData.coding_assessment_results[0].final_score}%
                                  </span>
                                  {assessmentData.coding_assessment_results[0].qualified ? 
                                    <span className="ml-2 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">Qualified</span> :
                                    <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">Not Qualified</span>
                                  }
                                </p>
                                {assessmentData.coding_assessment_results[0].time_taken && (
                                  <p className="text-sm text-slate-500">
                                    Time taken: {Math.floor(assessmentData.coding_assessment_results[0].time_taken / 60)}h {assessmentData.coding_assessment_results[0].time_taken % 60}m
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  </motion.div>
              </TabsContent>

              <TabsContent value="candidate-info" className="space-y-6" key="candidate-info">
                <motion.div 
                  className="space-y-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  {/* Bio Section */}
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-slate-600" />
                            </div>
                      <div className="flex-1">
                        <h2 className="text-lg font-semibold text-slate-900 mb-2">
                          About
                        </h2>
                        <p className="text-slate-700 leading-relaxed">
                          {candidate.bio || 'No bio available.'}
                            </p>
                          </div>
                            </div>
                          </div>

                 {/* Bento grid: two equal columns on large screens */}
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                  {/* Work Experience Section (equal width column) */}
                  <div className="bg-white rounded-xl border border-slate-200 p-5 h-full flex flex-col">
                     <div className="flex items-start gap-4 mb-4">
                       <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                         <Briefcase className="w-5 h-5 text-slate-600" />
                            </div>
                       <div className="flex-1">
                         <h2 className="text-lg font-semibold text-slate-900 mb-1">
                           Work Experience
                         </h2>
                         <p className="text-slate-600 text-sm">
                           Professional background and career progression
                            </p>
                          </div>
                      </div>

                     {candidate.work_experiences && candidate.work_experiences.length > 0 ? (
                       <div className="relative">
                         <div className="absolute left-6 top-0 bottom-0 w-px bg-slate-200" />
                         <ul className="space-y-5">
                          {candidate.work_experiences.map((exp, index) => (
                             <li key={index} className="relative pl-14">
                               <span className="absolute left-6 -translate-x-1/2 top-2.5 w-2.5 h-2.5 rounded-full bg-white border-2 border-slate-300 shadow-sm" />
                               <div className="flex items-start gap-4">
                                 <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                                {exp.company_logo ? (
                                    <img
                                      src={exp.company_logo}
                                      alt={`${exp.company} logo`}
                                       className="w-7 h-7 object-contain rounded-md"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                         target.parentElement!.innerHTML = `<div class=\"w-7 h-7 bg-slate-100 rounded-md flex items-center justify-center text-xs text-slate-600 font-semibold\">${exp.company?.charAt(0)?.toUpperCase() || '?'}<\/div>`;
                                      }}
                                    />
                                ) : (
                                     <div className="w-7 h-7 bg-slate-100 rounded-md flex items-center justify-center">
                                       <span className="text-xs text-slate-600 font-semibold">
                                      {exp.company?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                  </div>
                                )}
                              </div>
                                 <div className="flex-1 min-w-0">
                                   <h3 className="text-base font-semibold text-slate-900 mb-0.5">{exp.title}</h3>
                                   <p className="text-slate-700 font-medium mb-1">{exp.company}</p>
                                   <p className="text-xs text-slate-500 mb-2">{exp.startDate} - {exp.endDate || 'Present'}</p>
                                   {exp.description && (
                                     <p className="text-slate-700 leading-relaxed text-sm">{exp.description}</p>
                                   )}
                            </div>
                               </div>
                             </li>
                          ))}
                         </ul>
                        </div>
                      ) : (
                       <div className="text-center py-8">
                         <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                           <Briefcase className="w-8 h-8 text-slate-400" />
                         </div>
                         <p className="text-slate-500">No professional experience listed</p>
                       </div>
                     )}
                   </div>

                  {/* Right Column: two separate bento grid components */}
                  <div className="h-full flex flex-col gap-6">
                    {/* Skills Section */}
                    <div className="bg-white rounded-xl border border-slate-200 p-5 flex-1 flex flex-col">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                          <Code className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <h2 className="text-lg font-semibold text-slate-900 mb-1">
                            Skills & Technologies
                          </h2>
                          <p className="text-slate-600 text-sm">
                            Technical expertise and professional competencies
                          </p>
                        </div>
                      </div>
                        {candidate.skills && candidate.skills.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {candidate.skills.map(skill => (
                            <span
                              key={skill}
                              className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                            <Code className="w-8 h-8 text-slate-400" />
                          </div>
                          <p className="text-slate-500">No skills listed</p>
                        </div>
                        )}
                      </div>
                    {/* Education Section */}
                    <div className="bg-white rounded-xl border border-slate-200 p-5 flex-1 flex flex-col">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                          <GraduationCap className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <h2 className="text-lg font-semibold text-slate-900 mb-1">
                            Education
                          </h2>
                          <p className="text-slate-600 text-sm">
                            Academic background and qualifications
                          </p>
                          </div>
                      </div>
                      {candidate.educations && candidate.educations.length > 0 ? (
                        <div className="space-y-3">
                          {candidate.educations.map((edu, index) => (
                            <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                              <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                                {edu.institution_logo ? (
                                    <img
                                      src={edu.institution_logo}
                                      alt={`${edu.institution} logo`}
                                    className="w-7 h-7 object-contain rounded-md"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                      target.parentElement!.innerHTML = `<div class=\"w-7 h-7 bg-slate-100 rounded-md flex items-center justify-center text-xs text-slate-600 font-semibold\">${edu.institution?.charAt(0)?.toUpperCase() || '?'}<\/div>`;
                                      }}
                                    />
                                ) : (
                                  <div className="w-7 h-7 bg-slate-100 rounded-md flex items-center justify-center">
                                    <span className="text-xs text-slate-600 font-semibold">
                                      {edu.institution?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold text-slate-900 mb-0.5">
                                  {edu.degree}
                                </h3>
                                <p className="text-slate-700 font-medium mb-1">
                                  {edu.institution}
                                </p>
                                <p className="text-xs text-slate-500">
                                {edu.startDate} - {edu.endDate || 'Present'}
                              </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                            <GraduationCap className="w-8 h-8 text-slate-400" />
                          </div>
                          <p className="text-slate-500">No education listed</p>
                        </div>
                      )}
                </div>
                  </div>
                 </div>

                 {/* Projects & Portfolio (full width below bento grid) */}
                 <div className="bg-white rounded-xl border border-slate-200 p-5 h-full flex flex-col">
                   <div className="flex items-start gap-4 mb-4">
                     <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                       <FolderOpen className="w-5 h-5 text-slate-600" />
                     </div>
                     <div className="flex-1">
                       <h2 className="text-lg font-semibold text-slate-900 mb-1">
                         Projects & Portfolio
                       </h2>
                       <p className="text-slate-600 text-sm">
                         Personal and professional project showcase
                       </p>
                     </div>
                   </div>
                   {candidate.projects && candidate.projects.length > 0 ? (
                     <div className="space-y-3">
                       {candidate.projects.map((project, index) => (
                         <div
                           key={index}
                           className="bg-slate-50 border border-slate-200 rounded-xl p-4 hover:bg-slate-100 transition-colors"
                         >
                           <div className="flex items-start justify-between mb-2">
                             <h3 className="text-base font-semibold text-slate-900">
                               {project.name}
                             </h3>
                             {project.url && (
                               <a
                                 href={
                                   project.url?.startsWith('http')
                                     ? project.url
                                     : `https://${project.url}`
                                 }
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                               >
                                 View
                                 <ExternalLink className="w-3 h-3" />
                               </a>
                             )}
                           </div>
                           <p className="text-slate-700 leading-relaxed text-sm">
                             {project.description}
                           </p>
                         </div>
                       ))}
                     </div>
                   ) : (
                     <div className="text-center py-8">
                       <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                         <FolderOpen className="w-8 h-8 text-slate-400" />
                       </div>
                       <p className="text-slate-500">No projects listed</p>
                     </div>
                   )}
                 </div>
                </motion.div>
              </TabsContent>

              <TabsContent value="assessments" className="space-y-4" key="assessments">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                {selectedInterview ? (
                  <div className="space-y-6">
                    {/* Assessment Header */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-6">
                       <div className="flex items-start gap-4">
                         <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                           <BarChart3 className="w-6 h-6 text-slate-600" />
                         </div>
                         <div className="flex-1">
                           <h2 className="text-2xl font-semibold text-slate-900 mb-2">Assessment Rounds</h2>
                           <p className="text-slate-600">Comprehensive evaluation across multiple assessment stages</p>
                         </div>
                       </div>
                     </div>

                    <Tabs key={activeTab} defaultValue="round1" className="w-full" onValueChange={setActiveSubtab}>
                      <TabsList className="flex items-center gap-3 bg-gray-50/50 border border-gray-200 backdrop-blur-lg py-1 px-2 rounded-lg shadow-sm h-auto">

                      <TabsTrigger 
                        value="round1" 
                          className="relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-lg transition-colors text-gray-600 hover:text-blue-600/80 data-[state=active]:text-blue-600/90 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!evaluation?.experience_assessment && !selectedInterview?.transcript}
                      >
                          <div className="text-center">
                            <div className="font-medium text-sm">Round 1</div>
                            <div className="text-xs text-gray-500">Technical Interview</div>
                          </div>
                          {activeTab === "assessments" && activeSubtab === "round1" && (
                            <motion.div
                              layoutId="subtab-lamp"
                              className="absolute inset-0 w-full bg-blue-500/5 rounded-lg -z-10"
                              initial={false}
                              transition={{
                                type: "spring",
                                stiffness: 300,
                                damping: 30,
                              }}
                            >
                              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-6 h-1 bg-blue-500 rounded-t-full">
                                <div className="absolute w-8 h-4 bg-blue-500/20 rounded-lg blur-md -top-1 -left-1" />
                                <div className="absolute w-6 h-4 bg-blue-500/20 rounded-lg blur-md -top-0.5" />
                                <div className="absolute w-3 h-3 bg-blue-500/20 rounded-lg blur-sm top-0 left-1.5" />
                              </div>
                            </motion.div>
                          )}
                      </TabsTrigger>

                      <TabsTrigger 
                        value="round2" 
                          className="relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-lg transition-colors text-gray-600 hover:text-green-600/80 data-[state=active]:text-green-600/90 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!assessmentData}
                      >
                          <div className="text-center">
                            <div className="font-medium text-sm">Round 2</div>
                            <div className="text-xs text-gray-500">Coding Assessment</div>
                          </div>
                          {activeTab === "assessments" && activeSubtab === "round2" && (
                            <motion.div
                              layoutId="subtab-lamp"
                              className="absolute inset-0 w-full bg-green-500/5 rounded-lg -z-10"
                              initial={false}
                              transition={{
                                type: "spring",
                                stiffness: 300,
                                damping: 30,
                              }}
                            >
                              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-6 h-1 bg-green-500 rounded-t-full">
                                <div className="absolute w-8 h-4 bg-green-500/20 rounded-lg blur-md -top-1 -left-1" />
                                <div className="absolute w-6 h-4 bg-green-500/20 rounded-lg blur-md -top-0.5" />
                                <div className="absolute w-3 h-3 bg-green-500/20 rounded-lg blur-sm top-0 left-1.5" />
                              </div>
                            </motion.div>
                          )}
                      </TabsTrigger>

                      <TabsTrigger 
                        value="round3" 
                          className="relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-lg transition-colors text-gray-600 hover:text-purple-600/80 data-[state=active]:text-purple-600/90 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!evaluation?.job_assessments}
                      >
                          <div className="text-center">
                            <div className="font-medium text-sm">Round 3</div>
                            <div className="text-xs text-gray-500">Live Coding</div>
                          </div>
                          {activeTab === "assessments" && activeSubtab === "round3" && (
                            <motion.div
                              layoutId="subtab-lamp"
                              className="absolute inset-0 w-full bg-purple-500/5 rounded-lg -z-10"
                              initial={false}
                              transition={{
                                type: "spring",
                                stiffness: 300,
                                damping: 30,
                              }}
                            >
                              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-6 h-1 bg-purple-500 rounded-t-full">
                                <div className="absolute w-8 h-4 bg-purple-500/20 rounded-lg blur-md -top-1 -left-1" />
                                <div className="absolute w-6 h-4 bg-purple-500/20 rounded-lg blur-md -top-0.5" />
                                <div className="absolute w-3 h-3 bg-purple-500/20 rounded-lg blur-sm top-0 left-1.5" />
                              </div>
                            </motion.div>
                          )}
                      </TabsTrigger>

                    </TabsList>

                    <AnimatePresence mode="wait">
                      <TabsContent value="round1" className="space-y-4" key="round1">
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                      {evaluation?.experience_assessment || selectedInterview?.transcript ? (
                        <>
                {/* Experience Level Overview */}
                  <div className="bg-white rounded-xl border border-slate-200/60 p-6">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                        <BrainIcon className="w-6 h-6 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">
                              Experience Assessment
                            </h2>
                        <p className="text-slate-600">
                          AI-determined experience level and seniority assessment
                          </p>
                        </div>
                      <div className="text-right">
                        <div className="text-3xl font-semibold text-slate-800">
                          {evaluation?.experience_assessment?.candidateSeniorityScore || 'N/A'}
                        </div>
                        <div className="text-sm text-slate-500">/ 100</div>
                        <div className="mt-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            (evaluation?.experience_assessment?.candidateSeniorityScore || 0) >= 70
                              ? 'bg-emerald-100 text-emerald-700'
                              : (evaluation?.experience_assessment?.candidateSeniorityScore || 0) >= 50
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-rose-100 text-rose-700'
                          }`}>
                            {evaluation?.experience_assessment?.candidateSeniority || 'Not assessed'}
                            </span>
                          </div>
                        </div>
                      </div>

                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Overall Score</span>
                        <span className="font-medium text-slate-800">
                          {evaluation?.experience_assessment?.candidateSeniorityScore || 0}%
                            </span>
                          </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-500 ${
                            (evaluation?.experience_assessment?.candidateSeniorityScore || 0) >= 70
                              ? 'bg-emerald-500/70'
                              : (evaluation?.experience_assessment?.candidateSeniorityScore || 0) >= 50
                                ? 'bg-amber-500/70'
                                : 'bg-rose-500/70'
                          }`}
                          style={{ width: `${evaluation?.experience_assessment?.candidateSeniorityScore || 0}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* AI Assessment Summary */}
                    {evaluation?.experience_assessment?.executiveSummary && (
                    <div className="bg-white rounded-xl border border-slate-200/60 p-6 mt-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-6 h-6 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-slate-900 mb-3">
                            AI Assessment Summary
                          </h3>
                          <p className="text-slate-600 leading-relaxed">
                            {evaluation.experience_assessment.executiveSummary}
                          </p>
                        </div>
                        </div>
                      </div>
                    )}

                  {/* Interview Transcript and Recording (combined) */}
                  <div className="bg-white rounded-xl border border-slate-200/60 p-6 mt-6 flow-root">
                    {interviewVideoUrl && (
                      <div className="lg:float-right lg:w-[550px] lg:ml-6 mb-4">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
                            <Video className="w-5 h-5 text-slate-600" />
                  </div>
                          <div>
                            <h4 className="text-base font-semibold text-slate-900">Interview Recording</h4>
                            <p className="text-slate-600 text-xs">Video recording of the interview session</p>
                          </div>
                        </div>
                        <div className="rounded-lg overflow-hidden border border-slate-200">
                          <video controls className="w-full h-[360px] bg-black">
                            <source src={interviewVideoUrl} type="video/webm" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </div>
                    )}

                          {/* Interview Transcript */}
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">
                          Interview Transcript
                        </h3>
                        <p className="text-slate-600">
                      Complete conversation between AI interviewer and candidate
                        </p>
                      </div>
                    </div>
                    <div>
                              <div className="space-y-6 pb-10">
                      {(conversationDetails?.transcript &&
                      Array.isArray(conversationDetails.transcript)
                        ? conversationDetails.transcript
                        : selectedInterview?.transcript &&
                            Array.isArray(selectedInterview.transcript)
                          ? selectedInterview.transcript
                          : []
                      ).map((item: TranscriptEntry, index: number) => {
                        if (item.message === null) return null;
                        if (item.message === '...' || item.message === '\n')
                          return null;
                        return (
                          <div
                            key={index}
                            className="flex gap-4 cursor-pointer hover:opacity-80 transition-opacity duration-200"
                            onClick={() => {
                              if (
                                typeof item.time_in_call_secs === 'number' &&
                                duration > 0
                              ) {
                                // Set the timestamp to seek to - the audio player will handle it
                                setSeekToTimestamp(item.time_in_call_secs);
                              }
                            }}
                            title={
                              typeof item.time_in_call_secs === 'number'
                                ? `Jump to ${formatTime(item.time_in_call_secs)}`
                                : undefined
                            }
                          >
                            <Avatar className="w-8 h-8 mt-1">
                              <AvatarFallback
                                className={
                                  item.role === 'agent'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-green-100 text-green-700'
                                }
                              >
                                {item.role === 'agent'
                                  ? 'AI'
                                  : getInitials(
                                      candidate.candidate_name ?? 'C'
                                    )}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium text-sm">
                                  {item.role === 'agent'
                                    ? 'AI Interviewer'
                                    : candidate.candidate_name}
                                </span>
                                <Badge variant="outline">
                                  {item.role === 'agent'
                                    ? 'Interviewer'
                                    : 'Candidate'}
                                </Badge>
                              </div>
                              <div
                                className={`p-4 rounded-lg ${item.role === 'agent' ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}
                              >
                                <p className="text-gray-800 leading-relaxed">
                                  {item.message}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                              </div>
                            </div>
                        </>
                      ) : (
                        <div className="bg-white rounded-xl border border-slate-200/60 p-12 text-center">
                          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                            <BrainIcon className="w-8 h-8 text-slate-400" />
                          </div>
                          <p className="text-slate-500">No technical interview data available</p>
                        </div>
                      )}
                        </motion.div>
                      </TabsContent>

                      <TabsContent value="round2" className="space-y-4" key="round2">
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                      {assessmentData ? (
                  <CodingAssessmentResults
                    assessmentData={assessmentData}
                    loading={assessmentLoading}
                    error={assessmentError}
                  />
                ) : (
                  <div className="bg-white rounded-xl border border-slate-200/60 p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <Code className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500">No coding assessment data available</p>
                  </div>
                )}
                        </motion.div>
                      </TabsContent>

                      <TabsContent value="round3" className="space-y-4" key="round3">
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                      {evaluation?.job_assessments ? (
                        <JobAssessments data={evaluation?.job_assessments} />
                      ) : (
                        <div className="bg-white rounded-xl border border-slate-200/60 p-12 text-center">
                          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                            <Award className="w-8 h-8 text-slate-400" />
                          </div>
                          <p className="text-slate-500">No live coding assessment data available</p>
                        </div>
                      )}
                        </motion.div>
                      </TabsContent>
                    </AnimatePresence>
                  </Tabs>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-slate-200/60 p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <Award className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500">No interview selected to view assessment results</p>
                  </div>
                )}
                </motion.div>
              </TabsContent>

              <TabsContent value="review" className="space-y-6" key="review-2">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                <ReviewTab
                  selectedInterview={selectedInterview}
                  onAccordionChange={setOpenAccordion}
                  candidate={candidate}
                  codingAssessmentFinalScore={
                    assessmentData?.coding_assessment_results?.[0]?.final_score
                  }
                  codingAssessmentLoading={assessmentLoading}
                />
                  </motion.div>
              </TabsContent>
              </AnimatePresence>
            </Tabs>
          </div>

        </div>
      </div>

      {conversationAudioUrl && activeTab === 'assessments' && activeSubtab === 'round1' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <InterviewAudioPlayer
              audioUrl={conversationAudioUrl}
              onTimeUpdate={setCurrentTime}
              onLoadedMetadata={dur => {
                setDuration(dur);
                setIsAudioLoaded(true);
              }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              seekToTime={seekToTimestamp}
            />
    </div>
        </div>
      )}
    </motion.div>
  );
}

